import { z } from "zod";
import { FeedbackCauseSchema } from "./quality";

/** Keep only recognizable static navigation paths; opaque IDs and token segments are omitted. */
export function safeFeedbackUrl(value: string): string {
  const url = new URL(value);
  if (!/^https?:$/.test(url.protocol)) throw new Error("UNSUPPORTED_FEEDBACK_URL");
  const safeSegments = new Set("home dashboard seller seller-center sellercenter portal products product catalog orders order list edit create new settings account login signin signup register registration onboarding shop shops search help support overview manage management analytics business index index.html".split(" "));
  const segments: string[] = [];
  for (const part of url.pathname.split("/").filter(Boolean)) {
    if (!safeSegments.has(part.toLowerCase())) break;
    segments.push(part);
  }
  return url.origin + "/" + segments.join("/");
}

export const FeedbackWebsiteSnapshotSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("captured"), capturedAt: z.string().datetime(), url: z.string().url().max(2000), domain: z.string().max(253), title: z.string().max(500), platform: z.string().max(80).optional(), country: z.string().max(80).optional(), source: z.string().max(80).optional() }).strict(),
  z.object({ status: z.literal("unavailable"), capturedAt: z.string().datetime() }).strict(),
  z.object({ status: z.literal("historical_missing") }).strict(),
]);
export type FeedbackWebsiteSnapshot = z.infer<typeof FeedbackWebsiteSnapshotSchema>;
export const UserFeedbackStatusSchema = z.enum(["pending", "in_progress", "resolved"]);
export const UserFeedbackRequestSchema = z.object({
  clientMessageId: z.string().min(1).max(180), operationId: z.string().min(1).max(180),
  type: z.enum(["solved", "unsolved", "report"]), cause: FeedbackCauseSchema.optional(),
  question: z.string().trim().min(1).max(12000), displayedReply: z.string().max(24000),
  currentWebsite: z.object({ questionTime: FeedbackWebsiteSnapshotSchema, feedbackTime: FeedbackWebsiteSnapshotSchema }).strict(),
  conversationId: z.string().min(1).max(160).optional(), runId: z.string().min(1).max(180).optional(), caseId: z.string().min(1).max(180).optional(),
}).strict();
export type UserFeedbackRequest = z.infer<typeof UserFeedbackRequestSchema>;
export const UserFeedbackFilterSchema = z.object({ page: z.coerce.number().int().min(1).default(1), limit: z.coerce.number().int().min(1).max(100).default(30), userId: z.string().uuid().optional(), from: z.string().datetime().optional(), to: z.string().datetime().optional(), site: z.string().max(253).optional(), type: UserFeedbackRequestSchema.shape.type.optional(), status: UserFeedbackStatusSchema.optional() }).strict();
export type UserFeedbackFilter = z.infer<typeof UserFeedbackFilterSchema>;
export const UserFeedbackUpdateSchema = z.object({ status: UserFeedbackStatusSchema, note: z.string().trim().min(1).max(2000) }).strict();
export type UserFeedbackUpdate = z.infer<typeof UserFeedbackUpdateSchema>;
export interface UserFeedbackRecord extends UserFeedbackRequest {
  id: string; userId: string; submitter: string; createdAt: string; updatedAt: string;
  status: z.infer<typeof UserFeedbackStatusSchema>;
  serverTruth: { question: string; answer: string; status?: string; turnId?: string } | null;
  historyConversationId: string | null;
  audit: Array<{ actorId: string; createdAt: string; status: z.infer<typeof UserFeedbackStatusSchema>; note: string }>;
}
